/**
 * src/components/student-dashboard-components/theme/themeUtils.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import { themeConfig } from './themeConfig';

const warnedMissingColors = new Set();
/**
 * Should Warn.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const shouldWarn = () => process.env.NODE_ENV !== 'production';

/**
 * Get Color.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getColor = (colorPath) => {
  if (!colorPath) return '#000000';
  const path = String(colorPath).split('.');
  let result = themeConfig.colors;

  for (const key of path) {
    if (result && Object.prototype.hasOwnProperty.call(result, key)) {
      result = result[key];
    } else {
      if (shouldWarn() && !warnedMissingColors.has(colorPath)) {
        warnedMissingColors.add(colorPath);
        console.warn(`Color path "${colorPath}" not found in theme`);
      }
      return '#000000';
    }
  }

  return result;
};

/**
 * Get Typography.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getTypography = (variant, isMobile = false) => {
  const typography = isMobile ? themeConfig.typography.mobile : themeConfig.typography.desktop;
  return typography[variant] || typography.body;
};

/**
 * Get Spacing.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getSpacing = (size) => {
  return themeConfig.spacing[size] || size;
};

/**
 * Get Shadow.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getShadow = (size) => {
  return themeConfig.shadows[size] || themeConfig.shadows.md;
};

/**
 * Get Border Radius.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getBorderRadius = (size) => {
  return themeConfig.borderRadius[size] || themeConfig.borderRadius.md;
};

/**
 * Create Styled Component.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const createStyledComponent = (baseStyles, variants = {}) => {
  return (props) => {
    const { variant = 'default', isMobile, ...restProps } = props;
    
    const variantStyles = variants[variant] || {};
    const typography = getTypography('body', isMobile);
    
    return {
      ...typography,
      ...baseStyles,
      ...variantStyles,
      ...restProps
    };
  };
};

/**
 * Hex To Rgba.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const hexToRgba = (hex, alpha = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Lighten Color.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const lightenColor = (color, amount = 20) => {
  const colorWithoutHash = color.replace('#', '');
  const num = parseInt(colorWithoutHash, 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return `#${(0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16)
    .slice(1)}`;
};

/**
 * Darken Color.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const darkenColor = (color, amount = 20) => {
  return lightenColor(color, -amount);
};

/**
 * Create Media Query.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const createMediaQuery = (breakpoint) => {
  const size = themeConfig.breakpoints[breakpoint];
  return `@media (min-width: ${size})`;
};

/**
 * Is Desktop.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const isDesktop = () => window.innerWidth >= parseInt(themeConfig.breakpoints.lg);
/**
 * Is Tablet.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const isTablet = () => window.innerWidth >= parseInt(themeConfig.breakpoints.md) && window.innerWidth < parseInt(themeConfig.breakpoints.lg);
/**
 * Is Mobile.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const isMobile = () => window.innerWidth < parseInt(themeConfig.breakpoints.md);

/**
 * Get Responsive Value.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const getResponsiveValue = (mobileValue, tabletValue, desktopValue) => {
  if (isDesktop()) return desktopValue || tabletValue || mobileValue;
  if (isTablet()) return tabletValue || mobileValue;
  return mobileValue;
};

/**
 * Create Animation.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const createAnimation = (keyframes, duration = 'normal', easing = 'easeInOut') => {
  return {
    animation: `${keyframes} ${themeConfig.animations.duration[duration]} ${themeConfig.animations.easing[easing]}`
  };
};

/**
 * Create Transition.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */


export const createTransition = (property = 'all', duration = 'normal', easing = 'easeInOut') => {
  return {
    transition: `${property} ${themeConfig.animations.duration[duration]} ${themeConfig.animations.easing[easing]}`
  };
};
